import { getPublicKey, getMessages } from "../api";
const DEBUG = false;

export const toBase64 = (uint8Array) =>
  window.btoa(String.fromCharCode(...uint8Array));

export const fromBase64 = (base64) => {
  try {
    return Uint8Array.from(window.atob(base64), (c) => c.charCodeAt(0));
  } catch {
    return new Uint8Array();
  }
};

export const decryptMessage = async (ivBase64, messageBase64, sharedKeyRef) => {
  try {
    const iv = fromBase64(ivBase64);
    const encrypted = fromBase64(messageBase64);
    if (iv.length !== 12) throw new Error("Invalid IV length");

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      sharedKeyRef.current,
      encrypted
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    return "[Unreadable message]";
  }
};

export const setupEncryption = async (recipientMobile, identityPrivateKeyRef, sharedKeyRef) => {
  const storedPriv = localStorage.getItem("identityPrivateKey");
  if (!storedPriv) throw new Error("Missing identityPrivateKey");

  const rawPriv = fromBase64(storedPriv);
  identityPrivateKeyRef.current = await crypto.subtle.importKey(
    "pkcs8",
    rawPriv,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey"]
  );

  const res = await getPublicKey(recipientMobile);
  const recipientIdentityRaw = fromBase64(res.data.identityPublicKey);
  const recipientIdentityKey = await crypto.subtle.importKey(
    "raw",
    recipientIdentityRaw,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );

  sharedKeyRef.current = await crypto.subtle.deriveKey(
    { name: "ECDH", public: recipientIdentityKey },
    identityPrivateKeyRef.current,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

export const initializeWebSocket = async (
  token,
  socketRef,
  sharedKeyRef,
  myMobile,
  recipientMobile,
  setIsConnected,
  setMessages,
  onSignal // optional callback to receive signalling messages (e.g., offers)
) => {
  // If there's an existing socket, close it to avoid duplicate connections/listeners
  try {
    if (socketRef?.current) {
      try {
        socketRef.current.close();
      } catch (e) {}
      socketRef.current = null;
    }
  } catch (e) {}

  const ephemeralKeyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey"]
  );
  const ephemeralPubRaw = await crypto.subtle.exportKey("raw", ephemeralKeyPair.publicKey);
  const ephemeralPubBase64 = toBase64(new Uint8Array(ephemeralPubRaw));

  const ws = new WebSocket(
    `wss://6pwezj7gk8.execute-api.ap-south-1.amazonaws.com/prod?token=${token}&pub=${encodeURIComponent(ephemeralPubBase64)}`
  );

  socketRef.current = ws;

  // short-lived dedupe set for signalling frames (raw string)
  const recentSignals = new Set();

  const handleOpen = () => setIsConnected(true);
  const handleClose = () => setIsConnected(false);

  const handleMessage = async (e) => {
    try {
      const msg = JSON.parse(e.data);
      
      if (DEBUG) {
        console.log("[DEBUG] myMobile:", myMobile, "recipientMobile:", recipientMobile);
        if (msg && (msg.sender !== undefined || msg.recipient !== undefined)) {
          console.log("[DEBUG] msg.sender:", msg.sender, "msg.recipient:", msg.recipient);
        }
      }
  // (incoming message)
      // Basic debug for core events
      if (DEBUG) {
        try {
          if (msg && (msg.type === "typing" || msg.type === "receipt" || msg.type === "message-ack")) {
            console.debug("chatUtils: core event", msg.type);
          }
        } catch (e) {}
      }

      // Handle typing indicator messages
      if (msg && msg.type === "typing" && msg.from) {
        // Dispatch type-safe custom event for the page to consume
        const typingEvent = new CustomEvent("typing-status", {
          detail: { from: msg.from, isTyping: msg.isTyping }
        });
        window.dispatchEvent(typingEvent);
        return;
      }

      // Handle message acknowledgment (backend timestamp update)
      if (msg && msg.type === "message-ack") {
        const { messageId, timestamp: backendTimestamp, recipient } = msg;
        
        // Only process ack if it's for the current chat
        if (recipient !== recipientMobile) return;
        
        // Update the local message with backend's timestamp and id
        setMessages((prev) => 
          prev.map(m => {
            // Match by localId or recent timestamp (within 5 seconds)
            if (m.localId || (m.sender === myMobile && !m.id)) {
              const timeDiff = Math.abs(new Date(m.timestamp).getTime() - new Date(backendTimestamp).getTime());
              if (timeDiff < 5000) {
                return { ...m, timestamp: backendTimestamp, id: messageId, localId: undefined };
              }
            }
            return m;
          })
        );
        return;
      }

      // If it's an encrypted chat message with sender/recipient fields, process it
      if (msg && msg.sender !== undefined && msg.recipient !== undefined) {
        // Ignore messages not intended for me
        if (msg.recipient !== myMobile) return;
        if (msg.sender !== recipientMobile) return;

        const text = await decryptMessage(msg.iv, msg.message, sharedKeyRef);
        const newMessage = {
          sender: msg.sender,
          message: text,
          timestamp: msg.timestamp || new Date().toISOString(),
          status: msg.status || null,
          deliveredAt: msg.deliveredAt || null,
          seenAt: msg.seenAt || null,
          replyTo: msg.replyTo ? {
            sender: msg.replyTo.sender,
            message: msg.replyTo.message,
            timestamp: msg.replyTo.timestamp
          } : null
        };

        // Add media metadata if present
        if (msg.mediaType && msg.mediaUrl) {
          newMessage.mediaType = msg.mediaType;
          newMessage.mediaUrl = msg.mediaUrl;
          newMessage.mediaKey = msg.mediaKey;
          newMessage.mediaSize = msg.mediaSize;
          if (msg.mediaThumbnail) {
            newMessage.mediaThumbnail = msg.mediaThumbnail;
          }
          if (msg.mediaDuration) {
            newMessage.mediaDuration = msg.mediaDuration;
          }
        }

        setMessages((prev) => [...prev, newMessage]);

        // Send delivery receipt once upon receiving a message addressed to me
        try {
          socketRef.current?.send(
            JSON.stringify({
              action: "receipt",
              data: JSON.stringify({ type: "delivered", to: msg.sender, timestamp: newMessage.timestamp })
            })
          );
        } catch (e) { /* noop */ }

        // If the chat is currently in view, also send 'seen'
        try {
          if (document.visibilityState === 'visible') {
            setTimeout(() => {
              try {
                socketRef.current?.send(
                  JSON.stringify({
                    action: "receipt",
                    data: JSON.stringify({ type: "seen", to: msg.sender, timestamp: newMessage.timestamp })
                  })
                );
              } catch (_) {}
            }, 300);
          }
        } catch (_) {}
        return;
      }

      // Non-chat messages (signalling/receipts) -> update or forward
      if (msg && msg.type === 'receipt' && msg.messageKey && msg.status) {
        // Update my outgoing message status in-place
        const { sender, recipient, timestamp } = msg.messageKey;
        
        // messageKey.sender is the original sender (me), messageKey.recipient is who received it
        setMessages((prev) => 
          prev.map(m => {
            // Match messages I sent (sender === myMobile OR sender === 'You') with the timestamp from receipt
            if ((m.sender === myMobile || m.sender === 'You') && m.timestamp === timestamp) {
              // Update status and also update the time fields if present
              const updatedMsg = { 
                ...m, 
                status: msg.status,
                sender: m.sender // preserve existing sender value ('You' or myMobile)
              };
              if (msg.status === 'delivered' && msg.at) {
                updatedMsg.deliveredAt = msg.at;
              }
              if (msg.status === 'seen' && msg.at) {
                updatedMsg.seenAt = msg.at;
              }
              return updatedMsg;
            }
            return m;
          })
        );
        return;
      }

      // Non-chat messages (signalling) -> forward to onSignal if provided
      if (onSignal) {
        try {
          const raw = typeof e.data === "string" ? e.data : JSON.stringify(e.data);
          // (removed debug logging)
          if (!recentSignals.has(raw)) {
            recentSignals.add(raw);
            setTimeout(() => recentSignals.delete(raw), 5000);
            onSignal(e);
          }
        } catch (err) {
          try { onSignal(e); } catch (_) {}
        }
      }
    } catch (err) {
      // ignore parse errors
      if (onSignal) {
        try {
          const raw = typeof e.data === "string" ? e.data : JSON.stringify(e.data);
          if (!recentSignals.has(raw)) {
            recentSignals.add(raw);
            setTimeout(() => recentSignals.delete(raw), 5000);
            onSignal(e);
          }
        } catch (_) {
          try { onSignal(e); } catch (_) {}
        }
      }
    }
  };

  ws.addEventListener?.("open", handleOpen);
  ws.addEventListener?.("close", handleClose);
  ws.addEventListener?.("message", handleMessage);

  // keep socketRef.current set; callers who replace socket should close it first
};

export const sendEncryptedMessage = async (
  text,
  myMobile,
  recipientMobile,
  sharedKeyRef,
  socketRef,
  replyTo = null,
  mediaMetadata = null
) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(text);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    sharedKeyRef.current,
    encoded
  );

  const localId = Date.now().toString(36) + Math.random().toString(36).slice(2);

  const payload = {
    sender: myMobile,
    recipient: recipientMobile,
    iv: toBase64(iv),
    message: toBase64(new Uint8Array(encrypted)),
    localId,
    replyTo: replyTo ? {
      sender: replyTo.sender,
      message: replyTo.message,
      timestamp: replyTo.timestamp
    } : null
  };

  // Add media metadata if present
  if (mediaMetadata) {
    payload.mediaType = mediaMetadata.mediaType;
    payload.mediaUrl = mediaMetadata.mediaUrl;
    payload.mediaKey = mediaMetadata.mediaKey;
    payload.mediaSize = mediaMetadata.mediaSize;
    if (mediaMetadata.mediaThumbnail) {
      payload.mediaThumbnail = mediaMetadata.mediaThumbnail;
    }
    if (mediaMetadata.mediaDuration) {
      payload.mediaDuration = mediaMetadata.mediaDuration;
    }
  }

  socketRef.current.send(
    JSON.stringify({ action: "sendmessage", data: JSON.stringify(payload) })
  );

  return {
    localId,
    timestamp: new Date().toISOString(),
    status: 'sent',
  };
};
